import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { DataTableStatic } from "@/components/datatable";
import type { ColumnDef } from "@/components/datatable";
import {
  GetEditableColumns,
  UpdateEditableColumns,
} from "@/hooks/services/pps/handlers/editable-columns-api";
import {
  EditableColumnProperties,
  EditableColumns,
} from "@/hooks/services/pps/types/editable-columns.type";
import { useGetData, usePutData } from "@/hooks/services/use-query-hooks";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Settings, Save } from "lucide-react";

export const Route = createFileRoute("/settings/editable-columns/edit/")({
  component: RouteComponent,
});

function RouteComponent() {
  const editableColumnsResponse = useGetData(GetEditableColumns());
  const [editableColumns, setEditableColumns] = useState<EditableColumns[]>([]);
  const { mutate } = usePutData<EditableColumnProperties[], any>(
    UpdateEditableColumns(),
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (editableColumnsResponse.data && editableColumnsResponse.data.data) {
      const updatedProperties = editableColumnsResponse.data.data.map(
        (column: EditableColumns) => ({
          ...column,
          properties: {
            ...column.properties,
            isEditable: column.properties.isEditable,
          },
        }),
      );
      setEditableColumns(updatedProperties);
    }
  }, [editableColumnsResponse.data]);

  const setIsEditableColumns = (checked: boolean, columnLabel: string) => {
    setEditableColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.columnLabel === columnLabel
          ? {
              ...column,
              properties: {
                ...column.properties,
                isEditable: checked,
              },
            }
          : column,
      ),
    );
  };

  const columns: ColumnDef<EditableColumns>[] = [
    {
      accessorKey: "columnLabel",
      header: "Column",
      cell: ({ row }) => row.original.columnLabel,
    },
    {
      accessorKey: "properties.isEditable",
      header: "Is Editable",
      cell: ({ row }) => (
        <div className="aero:flex aero:items-center">
          <Switch
            id={`is-editable-${row.original.columnLabel}`}
            checked={row.original.properties.isEditable}
            onCheckedChange={(checked: boolean) => {
              setIsEditableColumns(checked, row.original.columnLabel);
            }}
          />
          <Label
            htmlFor={`is-editable-${row.original.columnLabel}`}
            className="aero:ml-2"
          >
            {row.original.properties.isEditable ? "Yes" : "No"}
          </Label>
        </div>
      ),
    },
  ];

  const handleSave = () => {
    const updatedColumns = editableColumns.map((column) => ({
      ...column.properties,
    }));

    mutate(updatedColumns, {
      onSuccess: () => {
        toast("Success", {
          description: "Editable columns updated successfully",
          className: "success-sonner",
          descriptionClassName: "success-sonner-description",
        });
        navigate({
          to: `/settings/editable-columns`,
        });
      },
      onError: (error) => {
        toast("Error", {
          description: error.message,
          className: "destructive-sonner",
          descriptionClassName: "destructive-sonner-description",
        });
      },
    });
  };

  return (
    <div className="aero:flex aero:flex-col aero:gap-4 aero:w-full aero:max-w-[1104px] aero:mx-auto">
      {/* Page Title */}
      <div className="aero:flex aero:items-center aero:gap-3">
        <div className="aero:w-10 aero:h-10 aero:rounded-lg aero:bg-brand-primary-50 aero:flex aero:items-center aero:justify-center">
          <Settings className="aero:text-brand-primary-500 aero:h-[27px] aero:w-[27px] aero:p-0.5" />
        </div>
        <h1 className="aero:text-xl aero:md:text-2xl aero:text-foreground aero:font-bold aero:leading-9">
          Edit Editable Columns
        </h1>
      </div>

      {/* Main Card */}
      <Card className="aero:!rounded-lg aero:!border-neutral-300 aero:!p-4 aero:!gap-2 aero:!shadow-none">
        {/* Header */}
        <div className="aero:flex aero:items-center aero:justify-between aero:pb-4 aero:border-b aero:border-neutral-400">
          <div className="aero:flex aero:items-center aero:gap-2">
            <span className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
            <h2 className="aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
              Configure Column Editability
            </h2>
          </div>
        </div>

        {/* Table Content */}
        <CardContent className="aero:!p-0 aero:!pt-4">
          <DataTableStatic
            data={editableColumns}
            columns={columns}
            features={{
              enableSelection: false,
              enablePagination: false,
              enableSorting: true,
              enableColumnDragAndDrop: false,
              enablePinning: false,
              enableResizing: false,
            }}
            defaultPageSize={100}
            isLoading={editableColumnsResponse.isLoading}
          />

          {/* Action Buttons */}
          <div className="aero:flex aero:justify-end aero:gap-2 aero:mt-6">
            <Button
              type="button"
              design="outline"
              intent="neutral"
              onClick={() => navigate({ to: "/settings/editable-columns" })}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="aero:flex aero:items-center aero:gap-2 aero:bg-brand-primary-700 aero:hover:bg-brand-primary-800"
            >
              <Save className="aero:h-4 aero:w-4" />
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
